<template>
  <div class="page-container">
    <div class="filter">
      <span class="filter-title">Filter by Name</span>
      <BFormInput
        v-model="filterName"
        placeholder="Search by name"
        style="width: 15rem; margin-right: 1rem"
      />
      <span class="filter-title">Filter by Assignee</span>
      <BFormSelect
        v-model="filterAssignee"
        style="width: 15rem"
        :options="assigneeOptions"
        @focus="refreshAssigneeList"
      />
      <span
        class="filter-title"
        style="margin-left: 1rem"
      >Filter by State</span>
      <BFormSelect
        v-model="filterStateOption"
        style="width: 18rem; margin-left: .5rem"
        :options="stateDropdownOptions"
      />
    </div>

    <BRow>
      <BCol>
        <BTable
          v-model:sort-by="tableSortBy"
          :items="displayedReports"
          :fields="displayFields"
          :primary-key="$const.SLIDE_KEYS.SLIDE_ID"
          hover
          :busy="isBusy"
          no-local-sorting
          @row-clicked="selectSlide"
        >
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <BSpinner
                variant="primary"
                class="align-middle"
              />
            </div>
          </template>

          <template #cell(last_check_time)="{ value }">
            {{ formatLastCheckTime(value) }}
          </template>

          <template #cell(state)="{ value }">
            {{ resolveStateLabel(value) }}
          </template>

          <template #cell(assignee)="data">
            <BButton
              v-if="data.value === '' || data.value == null"
              size="sm"
              variant="primary"
              @click.stop.prevent="assignMe(data)"
            >
              {{ assignBtnText(data) }}
            </BButton>
            <div
              v-else
              class="assignee-cell"
            >
              <div
                class="assignee-value"
                :title="resolveAssigneeString(data.value)"
              >
                {{ resolveAssigneeString(data.value) }}
              </div>
              <a
                v-if="data.item && data.item.state !== $const.TASK_STATES.DONE"
                class="withdraw-link"
                href="#"
                @click.prevent.stop="confirmWithdrawTask(data)"
              >Withdraw</a>
            </div>
          </template>

          <template #cell(action)="data">
            <span v-if="data.item && data.item.state !== $const.TASK_STATES.DONE">
              <a
                href="#"
                @click.prevent.stop="confirmDelete(data)"
              >Delete</a>
            </span>
          </template>
        </BTable>
      </BCol>
    </BRow>

    <div ref="scrollSentinel" />
    <div
      v-if="isLoadingMore"
      class="d-flex justify-content-center py-2"
    >
      <BSpinner
        variant="secondary"
        small
      />
    </div>

    <BModal
      v-model="showDeleteModal"
      title="Confirm Deletion"
      :hide-footer="true"
    >
      <div class="d-flex align-items-start">
        <div class="modal-warning-icon">
          &#9888;
        </div>
        <div>
          <p style="color: #dc3545; font-weight: 600;">
            This action will delete the task
          </p>
          <p style="color: #dc3545;">
            Deleted tasks cannot be recovered. Please proceed with caution.
          </p>
        </div>
      </div>
      <template #footer>
        <div class="modal-footer-actions">
          <BButton
            variant="secondary"
            @click="showDeleteModal = false"
          >
            Cancel
          </BButton>
          <BButton
            variant="danger"
            @click="proceedDelete"
          >
            Delete
          </BButton>
        </div>
      </template>
    </BModal>

    <BModal
      v-model="showWithdrawModal"
      title="Withdraw"
      :hide-footer="true"
    >
      <div class="d-flex align-items-start">
        <div class="modal-withdraw-icon">
          &#9888;
        </div>
        <div>
          <p class="font-weight-bold">
            Withdraw the current assignee from this task
          </p>
          <p>Changes in the task are saved and can be reviewed by the next assignee.</p>
        </div>
      </div>
      <template #footer>
        <div class="modal-footer-actions">
          <BButton
            variant="secondary"
            @click="showWithdrawModal = false"
          >
            Cancel
          </BButton>
          <BButton
            variant="primary"
            @click="proceedWithdrawTask"
          >
            Withdraw
          </BButton>
        </div>
      </template>
    </BModal>

    <BModal
      v-model="showErrorModal"
      title="Error"
      :hide-footer="true"
      @hidden="handleErrorModalHidden"
    >
      {{ errorMsg }}
      <template #footer>
        <div class="modal-footer-actions">
          <BButton
            variant="primary"
            @click="handleErrorModalOk"
          >
            OK
          </BButton>
        </div>
      </template>
    </BModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useDashboardStore, useConfigStore } from '@/store';
import { useTime } from '@/composables/useTime';
import { useTaskPagination, type TaskFilters } from '@/composables/useTaskPagination';
import { useInfiniteScroll } from '@/composables/useInfiniteScroll';
import {
  BFormInput,
  BFormSelect,
  BRow,
  BCol,
  BTable,
  BSpinner,
  BButton,
  BModal,
  useToast,
} from 'bootstrap-vue-next';
import type { BTableSortBy } from 'bootstrap-vue-next';
import type { TaskSummary } from '@/types/api';
import { useRouter } from 'vue-router';
import { useApi, useConstants, useCookie } from '@/composables/useAppGlobals';

const dashboard = useDashboardStore();
const config = useConfigStore();
const $api = useApi();
const $const = useConstants();
const $cookie = useCookie();
const router = useRouter();

const { slides, hasMore, isBusy, isLoadingMore, loadTasks, loadMore, patchSlide, removeSlide } = useTaskPagination();
const scrollSentinel = ref<HTMLElement | null>(null);
useInfiniteScroll(scrollSentinel, () => {
  if (hasMore.value && !isLoadingMore.value && !isBusy.value) void loadMore(currentFilters.value);
});

type TableSlotContext<T> = {
  item?: T;
  value?: unknown;
  index?: number;
};

// local reactive state
const errorMsg = ref<string | null>(null);
const assigneeOptions = ref<string[]>(['all']);
const pendingDelete = ref<string | null>(null);
const pendingWithdraw = ref<string | null>(null);
const showDeleteModal = ref(false);
const showWithdrawModal = ref(false);
const showErrorModal = ref(false);

const dateTimeFormat = useTime().dateTimeFormat;
type DateTimeArg = Parameters<typeof dateTimeFormat>[0];

function formatLastCheckTime(value: unknown) {
  if (value == null) return '';
  if (typeof value === 'string' && value.trim() === '') return '';
  return dateTimeFormat(value as DateTimeArg);
}

function isTaskState(value: string): value is TaskSummary['state'] {
  const C = $const;
  return (
    value === C.TASK_STATES.IN_PROGRESS ||
    value === C.TASK_STATES.SCREEN ||
    value === C.TASK_STATES.RELABELING ||
    value === C.TASK_STATES.FINAL_REVIEW ||
    value === C.TASK_STATES.DONE
  );
}

function resolveStateLabel(value: unknown) {
  if (typeof value !== 'string') return '';
  if (!isTaskState(value)) return value;
  return addOrderBeforeState(value);
}

function resolveAssigneeString(value: unknown) {
  return typeof value === 'string' ? value : '';
}
const toast = useToast();

function handleActionFailure(message: string) {
  errorMsg.value = message;
  showErrorModal.value = true;
}

const filterName = computed({
  get: () => dashboard.filterName,
  set: (v: string) => dashboard.setFilterName(v),
});


const filterAssignee = computed({
  get: () => dashboard.filterAssignee,
  set: (v: string) => dashboard.setFilterAssignee(v),
});

const filterStateOption = computed({
  get: () => dashboard.filterStateOption,
  set: (v: string | null) => dashboard.setFilterStateOption(v),
});

const sortDesc = computed({
  get: () => dashboard.sortDesc,
  set: (v: boolean) => dashboard.setSortDesc(v),
});

const sortBy = computed({
  get: () => dashboard.sortBy,
  set: (v: string) => dashboard.setSortBy(v),
});

const tableSortBy = computed({
  get(): BTableSortBy[] {
    const order: BTableSortBy['order'] = sortDesc.value ? 'desc' : 'asc';
    return [{ key: sortBy.value || 'priority', order }];
  },
  set(criteria: BTableSortBy[] | BTableSortBy | null) {
    const list = Array.isArray(criteria) ? criteria : criteria ? [criteria] : [];
    if (!list.length) {
      sortBy.value = 'priority';
      sortDesc.value = false;
      return;
    }
    const primary = list[0];
    if (primary?.key) sortBy.value = primary.key;
    sortDesc.value = primary?.order === 'desc';
  },
});

const currentFilters = computed<TaskFilters>(() => ({
  name: filterName.value,
  assignee: filterAssignee.value,
  state: filterStateOption.value,
}));

const SORTABLE_KEYS: Array<keyof TaskSummary> = [
  'name',
  'state',
  'priority',
  'cells',
  'reviewer',
  'last_check_time',
  'assignee',
];

const displayedReports = ref<TaskSummary[]>([]);

let cachedSortedIds: string[] = [];
let lastSortParams = '';
let forceResort = false;

watch(isBusy, (busy) => {
  if (busy) forceResort = true;
});

watch([slides, sortBy, sortDesc], () => {
  const currentSortParams = `${sortBy.value}-${sortDesc.value}`;
  const isSortChange = currentSortParams !== lastSortParams;
  lastSortParams = currentSortParams;

  const needsFullSort = isSortChange || forceResort || cachedSortedIds.length === 0;
  if (!isBusy.value && forceResort) {
    forceResort = false;
  }

  const key = SORTABLE_KEYS.includes(sortBy.value as keyof TaskSummary)
    ? (sortBy.value as keyof TaskSummary)
    : 'priority';
  const compare = (a: TaskSummary, b: TaskSummary) => {
    const A = a[key], B = b[key];
    if (A === B) return 0;
    if (A == null) return 1;
    if (B == null) return -1;
    if (typeof A === 'number' && typeof B === 'number') return A - B;
    return String(A).localeCompare(String(B));
  };

  if (needsFullSort) {
    const sorted = [...slides.value].sort((a, b) => sortDesc.value ? compare(b, a) : compare(a, b));
    cachedSortedIds = sorted.map(s => s.id);
    displayedReports.value = sorted;
    return;
  }

  const slideMap = new Map(slides.value.map(s => [s.id, s]));
  const result: TaskSummary[] = [];
  const seenIds = new Set<string>();

  for (const id of cachedSortedIds) {
    if (slideMap.has(id)) {
      result.push(slideMap.get(id)!);
      seenIds.add(id);
    }
  }

  for (const item of slides.value) {
    if (!seenIds.has(item.id)) {
      result.push(item);
      cachedSortedIds.push(item.id);
    }
  }

  if (result.length !== cachedSortedIds.length) {
    cachedSortedIds = result.map(s => s.id);
  }

  displayedReports.value = result;
}, { deep: true, immediate: true, flush: 'sync' });

const displayFields = computed(() => {
  const thClass = 'th-class';
  const tdClass = 'td-class';
  return [
    { key: 'name', label: 'NAME', thClass, tdClass, sortable: true },
    { key: 'state', label: 'STATE', thClass, tdClass, sortable: true },

    { key: 'cells', label: 'NUM ITEMS', thClass, tdClass, sortable: true },
    { key: 'reviewer', label: 'REVIEWER', thClass, tdClass, sortable: true },
    { key: 'last_check_time', label: 'LAST CHECK TIME', thClass, tdClass, sortable: true },
    { key: 'assignee', label: 'ASSIGNEE', thClass, tdClass, sortable: true, thStyle: 'width: 160px; max-width: 160px; padding-right: 0.5rem;', tdStyle: 'width: 160px; max-width: 160px; padding-right: 0.5rem;' },
    { key: 'action', label: 'ACTION', thClass, tdClass },
  ];
});

const stateDropdownOptions = computed(() => {
  const C = $const;
  return [
    { text: 'All', value: null },
    { text: 'In Progress', value: C.TASK_STATES.IN_PROGRESS },
    { text: '    1/3-Screening', value: C.TASK_STATES.SCREEN },
    { text: '    2/3-Relabeling', value: C.TASK_STATES.RELABELING },
    { text: '    3/3-Final-Review', value: C.TASK_STATES.FINAL_REVIEW },
    { text: 'Complete', value: C.TASK_STATES.DONE },
  ];
});

function addOrderBeforeState(state: TaskSummary['state']) {
  const C = $const;
  if (state === C.TASK_STATES.DONE) return state;
  if (
    state === C.TASK_STATES.SCREEN ||
    state === C.TASK_STATES.RELABELING ||
    state === C.TASK_STATES.FINAL_REVIEW
  ) {
    const order = C.TASK_STATES_ORDER[state];
    return `${order}/${C.STATES_COUNT}-${state}`;
  }
  return state;
}

function isRecordReadOnly(record: Partial<TaskSummary> | null | undefined) {
  const assignee = record?.assignee ?? '';
  const state = record?.state;
  const userName = $cookie.getCookie($const.COOKIES.KEY.USER_NAME);
  return assignee !== userName || state === $const.TASK_STATES.DONE;
}

function assignBtnText(data: TableSlotContext<TaskSummary>) {
  const state = data?.item?.state ??
    (typeof data?.index === 'number' ? displayedReports.value[data.index]?.state : undefined) ??
    '';
  return state === $const.TASK_STATES.FINAL_REVIEW ? 'Final Review' : 'Assign Me';
}

function updateAssigneeOptionsFromStore() {
  const users = config.users ?? [];
  const list = new Set<string>(['all']);
  users.forEach((user) => {
    if (user) list.add(user);
  });
  assigneeOptions.value = Array.from(list);
  if (!assigneeOptions.value.includes(filterAssignee.value)) filterAssignee.value = 'all';
}

async function refreshAssigneeList() {
  try {
    const freshUsers = await $api.getActiveUsers();
    const current = new Set(config.users);
    const fresh = new Set(freshUsers);
    const unchanged = current.size === fresh.size && freshUsers.every(u => current.has(u));
    if (unchanged) return;
    config.setUsers(freshUsers);
  } catch {
    // silently keep the current list on network error
  }
}

async function assignMe(data: TableSlotContext<TaskSummary>) {
  const item = data?.item ?? null;
  if (!item) return;
  const taskId = item.id;
  const userName = $cookie.getCookie($const.COOKIES.KEY.USER_NAME);
  const userId = $cookie.getCookie($const.COOKIES.KEY.USER_ID);
  const original = slides.value.find(s => s.id === taskId);
  patchSlide(taskId, { assignee: userName ?? '', assignee_id: userId ?? undefined });
  try {
    const updated = await $api.updateTask(taskId, {
      [$const.SLIDE_KEYS.ASSIGNEE]: userName,
      [$const.SLIDE_KEYS.ASSIGNEE_ID]: userId,
    });
    const patchData = { ...updated };
    if (original && typeof patchData.cells !== 'number') {
      patchData.cells = original.cells;
    }
    patchSlide(taskId, patchData);
    toast.show({
      title: 'Task has been assigned to me',
      body: 'Task has been assigned to me',
      solid: true,
      autoHideDelay: 5000,
      toaster: 'b-toaster-top-right',
    });
  } catch {
    if (original) patchSlide(taskId, original);
    handleActionFailure('Assign failed (May be assigned to other person)');
  }
}

function confirmDelete(data: TableSlotContext<TaskSummary>) {
  const item = data?.item ?? null;
  if (!item) return;
  pendingDelete.value = item.id;
  showDeleteModal.value = true;
}

async function proceedDelete() {
  if (!pendingDelete.value) return;
  const taskId = pendingDelete.value;
  try {
    await $api.deleteTask(taskId);
    removeSlide(taskId);
    toast.show({
      title: 'Deleted',
      body: 'Task deleted',
      solid: true,
      autoHideDelay: 5000,
      toaster: 'b-toaster-top-right',
    });
    return true;
  } catch {
    handleActionFailure('Delete failed');
    return false;
  } finally {
    pendingDelete.value = null;
    showDeleteModal.value = false;
  }
}

function confirmWithdrawTask(data: TableSlotContext<TaskSummary>) {
  const item = data?.item ?? null;
  if (!item) return;
  pendingWithdraw.value = item.id;
  showWithdrawModal.value = true;
}

async function proceedWithdrawTask() {
  if (!pendingWithdraw.value) return;
  const taskId = pendingWithdraw.value;
  const original = slides.value.find(s => s.id === taskId);
  patchSlide(taskId, { assignee: '', assignee_id: undefined });
  try {
    const updated = await $api.updateTask(taskId, {
      [$const.SLIDE_KEYS.ASSIGNEE]: '',
      [$const.SLIDE_KEYS.ASSIGNEE_ID]: '',
    });
    const patchData = { ...updated };
    if (original && typeof patchData.cells !== 'number') {
      patchData.cells = original.cells;
    }
    patchSlide(taskId, patchData);
    toast.show({
      title: 'Task has been withdrawn',
      body: 'Task has been withdrawn',
      solid: true,
      autoHideDelay: 5000,
      toaster: 'b-toaster-top-right',
    });
    return true;
  } catch {
    if (original) patchSlide(taskId, original);
    handleActionFailure('Withdraw failed');
    return false;
  } finally {
    pendingWithdraw.value = null;
    showWithdrawModal.value = false;
    const currentSlideId = config.slideInfo[$const.SLIDE_KEYS.SLIDE_ID];
    if (typeof currentSlideId === 'string' && currentSlideId === taskId) {
      const readOnly = isRecordReadOnly(config.slideInfo as Partial<TaskSummary>);
      config.setIsReadOnly(readOnly);
    }
  }
}

function handleErrorModalOk() {
  showErrorModal.value = false;
  errorMsg.value = null;
}

function handleErrorModalHidden() {
  if (showErrorModal.value) return;
  errorMsg.value = null;
}

function selectSlide(record: TaskSummary) {
  const readOnly = isRecordReadOnly(record);
  config.setIsReadOnly(readOnly);
  config.setIsBusy(true);
  config.setSlideInfo(record as unknown as Record<string, unknown>);
  config.setIsSlideSelected(true);
  router.push($const.ROUTES.CELL);
}

let nameDebounceTimer: ReturnType<typeof setTimeout> | null = null;

watch(filterName, () => {
  if (nameDebounceTimer) clearTimeout(nameDebounceTimer);
  nameDebounceTimer = setTimeout(() => void loadTasks(currentFilters.value), 300);
});

watch([filterAssignee, filterStateOption], () => {
  void loadTasks(currentFilters.value);
});

watch(() => config.users, () => {
  updateAssigneeOptionsFromStore();
});

onMounted(async () => {
  config.setIsBusy(false);
  await loadTasks(currentFilters.value);
  updateAssigneeOptionsFromStore();
});

onBeforeUnmount(() => {
  if (nameDebounceTimer) clearTimeout(nameDebounceTimer);
});
</script>

<style lang="scss" scoped>
@use "@/assets/scss/common/alignment" as *;

.col { padding-left: 0px; }
.page-container { margin-top: 27px; }
.filter { display: flex; }
.filter-title { padding-right: 0.5rem ; padding-top: .375rem; padding-bottom: .375rem; line-height: 1.5; display: table-cell; vertical-align: middle; }
.assignee-cell { position: relative; height: 100%; min-height: 2.6rem; overflow: hidden; }
.assignee-value { position: absolute; left: 0; right: 0; top: 50%; transform: translateY(-50%); text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; padding-right: 1rem; }
.withdraw-link { position: absolute; left: 0; bottom: 0px; font-size: 12px; text-align: left; }
.modal-warning-icon { font-size: 32px; color: #dc3545; margin-right: 12px; }
.modal-withdraw-icon { font-size: 32px; color: black; margin-right: 12px; }
.modal-footer-actions { display: flex; justify-content: flex-end; gap: 0.75rem; width: 100%; padding: 0.75rem 1rem 0.5rem; }

:deep(.table td) { border-bottom: 1px solid #dee2e6; vertical-align: middle; height: 63px; }
:deep(.table-hover tbody tr:hover){ cursor: pointer; }

</style>
